// T084: Create edit post dialog
// T087: Pre-fill editor with existing content

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MarkdownEditor } from './MarkdownEditor'
import { ImageUpload } from './ImageUpload'
import { useUpdatePost } from '@/hooks/usePost'
import { extractImagesFromMarkdown } from '@/services/imageUpload'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import type { Post } from '@/types/post'

interface EditPostDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  post: Post
}

const MIN_CONTENT_LENGTH = 100

export function EditPostDialog({ open, onOpenChange, post }: EditPostDialogProps) {
  // Use key prop on content to reset form state when post changes
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <EditPostDialogContent
        key={post.permlink}
        post={post}
        onOpenChange={onOpenChange}
      />
    </Dialog>
  )
}

interface EditPostDialogContentProps {
  post: Post
  onOpenChange: (open: boolean) => void
}

function EditPostDialogContent({ post, onOpenChange }: EditPostDialogContentProps) {
  const [title, setTitle] = useState(post.title)
  const [body, setBody] = useState(post.body)
  const [tags, setTags] = useState(post.tags.join(', '))
  const [uploadedImages, setUploadedImages] = useState<string[]>(post.images || [])

  const updatePost = useUpdatePost()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (body.length < MIN_CONTENT_LENGTH) {
      toast.error(`Post must be at least ${MIN_CONTENT_LENGTH} characters long`)
      return
    }

    try {
      const markdownImages = extractImagesFromMarkdown(body)
      const allImages = [...new Set([...uploadedImages, ...markdownImages])]

      const tagList = tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean)

      await updatePost.mutateAsync({
        permlink: post.permlink,
        title: title.trim() || undefined,
        body,
        tags: tagList.length > 0 ? tagList : undefined,
        images: allImages.length > 0 ? allImages : undefined,
      })

      toast.success('Post updated successfully!')
      onOpenChange(false)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update post'
      toast.error('Failed to update post', {
        description: errorMessage,
      })
    }
  }

  const isFormValid = body.length >= MIN_CONTENT_LENGTH
  const isSubmitting = updatePost.isPending

  return (
    <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Edit Post</DialogTitle>
        <DialogDescription>
          Make changes to your post. Changes will be published immediately.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="edit-title">Title</Label>
          <Input
            id="edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post title"
            maxLength={255}
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-content">Content</Label>
          <MarkdownEditor
            value={body}
            onChange={setBody}
            minLength={MIN_CONTENT_LENGTH}
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-tags">Tags</Label>
          <Input
            id="edit-tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="tag1, tag2, tag3"
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label>Images</Label>
          <ImageUpload
            onImageUploaded={(url) => {
              setUploadedImages((prev) => [...prev, url])
              setBody((prev) => prev + `\n![Image](${url})\n`)
            }}
            onRemove={(url) => setUploadedImages((prev) => prev.filter((img) => img !== url))}
            uploadedImages={uploadedImages}
          />
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  )
}
